import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Loader2, Receipt, Search, Eye } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (n) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(n || 0);

const PayStubs = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [stubs, setStubs] = useState([]);
  const [search, setSearch] = useState("");
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/employees`, { headers });
        const list = res.data || [];
        setEmployees(list);
        if (list.length > 0) setSelectedEmployeeId(list[0].id);
      } catch (e) {
        toast.error("Failed to load employees");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!selectedEmployeeId) return;
    const fetchStubs = async () => {
      try {
        const params = year && year !== "all" ? `?year=${year}` : "";
        const res = await axios.get(
          `${API}/payroll/employees/${selectedEmployeeId}/pay-stubs${params}`,
          { headers }
        );
        setStubs(res.data?.pay_stubs || []);
      } catch (e) {
        setStubs([]);
      }
    };
    fetchStubs();
  }, [selectedEmployeeId, year]);

  const filteredEmployees = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return employees;
    return employees.filter(
      (e) =>
        e.name?.toLowerCase().includes(term) ||
        e.email?.toLowerCase().includes(term) ||
        e.position?.toLowerCase().includes(term)
    );
  }, [employees, search]);

  const totals = useMemo(() => {
    return stubs.reduce(
      (acc, s) => ({
        gross: acc.gross + (s.gross_pay || 0),
        deductions: acc.deductions + (s.total_deductions || 0),
        net: acc.net + (s.net_pay || 0),
      }),
      { gross: 0, deductions: 0, net: 0 }
    );
  }, [stubs]);

  const openDetail = async (stub) => {
    setDetail(null);
    setDetailLoading(true);
    setDetailOpen(true);
    try {
      const res = await axios.get(
        `${API}/payroll/pay-stubs/${stub.period_id}/${stub.employee_id}`,
        { headers }
      );
      setDetail(res.data?.pay_stub || null);
    } catch (e) {
      toast.error("Failed to load pay stub details");
    } finally {
      setDetailLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="pay-stubs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Receipt className="h-6 w-6" />
            Pay Stubs
          </h1>
          <p className="text-muted-foreground">
            Review approved &amp; paid payroll runs by employee. Stubs are generated from
            finalized periods only.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Employee picker */}
        <Card className="lg:col-span-1" data-testid="paystub-employee-picker">
          <CardHeader>
            <CardTitle className="text-base">Employees</CardTitle>
            <CardDescription>Select to view stubs</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search employees…"
                className="pl-8 h-9"
                data-testid="paystub-employee-search"
              />
            </div>
            <div className="space-y-1 max-h-[480px] overflow-y-auto pr-1">
              {filteredEmployees.length === 0 && (
                <p className="text-xs text-muted-foreground py-4 text-center">No employees</p>
              )}
              {filteredEmployees.map((e) => (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => setSelectedEmployeeId(e.id)}
                  className={`w-full text-left p-2 rounded-md border text-sm transition-colors ${
                    selectedEmployeeId === e.id
                      ? "border-primary bg-primary/10"
                      : "hover:bg-muted"
                  }`}
                  data-testid={`paystub-employee-${e.id}`}
                >
                  <p className="font-medium truncate">{e.name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {e.position || "—"}
                  </p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Stubs list */}
        <Card className="lg:col-span-3">
          <CardHeader>
            <div className="flex items-start justify-between gap-3">
              <div>
                <CardTitle>
                  {employees.find((e) => e.id === selectedEmployeeId)?.name || "Pay Stubs"}
                </CardTitle>
                <CardDescription>
                  {stubs.length} stub{stubs.length === 1 ? "" : "s"} ·{" "}
                  {totals.net > 0 ? `Net YTD ${formatCurrency(totals.net)}` : "No data"}
                </CardDescription>
              </div>
              <Select value={year} onValueChange={setYear}>
                <SelectTrigger className="w-32" data-testid="paystub-year-select">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {Array.from({ length: 4 }).map((_, i) => {
                    const y = String(new Date().getFullYear() - i);
                    return (
                      <SelectItem key={y} value={y}>
                        {y}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {stubs.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Receipt className="h-12 w-12 mx-auto mb-3 opacity-40" />
                <p className="text-sm">No pay stubs yet</p>
                <p className="text-xs mt-1">
                  Stubs appear here once a payroll run is approved or paid.
                </p>
              </div>
            ) : (
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Period</TableHead>
                      <TableHead>Pay Date</TableHead>
                      <TableHead className="text-right">Hours</TableHead>
                      <TableHead className="text-right">Gross</TableHead>
                      <TableHead className="text-right">Deductions</TableHead>
                      <TableHead className="text-right">Net</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stubs.map((s) => (
                      <TableRow key={s.id} data-testid={`paystub-row-${s.id}`}>
                        <TableCell className="text-sm">
                          {s.period_start} – {s.period_end}
                        </TableCell>
                        <TableCell className="text-sm">{s.pay_date || "—"}</TableCell>
                        <TableCell className="text-right">
                          {(s.regular_hours || 0).toFixed(1)}
                          {s.overtime_hours ? ` (+${s.overtime_hours.toFixed(1)} OT)` : ""}
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(s.gross_pay)}</TableCell>
                        <TableCell className="text-right text-red-600">
                          -{formatCurrency(s.total_deductions)}
                        </TableCell>
                        <TableCell className="text-right font-medium text-emerald-700">
                          {formatCurrency(s.net_pay)}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={
                              s.status === "paid"
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                : "bg-blue-50 text-blue-700 border-blue-200"
                            }
                          >
                            {s.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openDetail(s)}
                            data-testid={`paystub-view-${s.id}`}
                          >
                            <Eye className="h-4 w-4 mr-1" /> View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Pay Stub Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl" data-testid="paystub-detail-dialog">
          <DialogHeader>
            <DialogTitle>Pay Stub Detail</DialogTitle>
            <DialogDescription>
              {detail?.period?.start_date} – {detail?.period?.end_date}
            </DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : !detail ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Unable to load this stub.
            </p>
          ) : (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-md bg-muted/40">
                  <p className="text-xs text-muted-foreground">Employee</p>
                  <p className="font-medium">{detail.employee?.name}</p>
                </div>
                <div className="p-3 rounded-md bg-muted/40">
                  <p className="text-xs text-muted-foreground">Employer</p>
                  <p className="font-medium">
                    {detail.company?.name || "Business"}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-md border p-3">
                  <p className="text-xs text-muted-foreground mb-2 font-medium uppercase">
                    Earnings
                  </p>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span>Regular ({detail.earnings?.regular_hours || 0} hrs)</span>
                      <span>{formatCurrency(detail.earnings?.gross_pay - (detail.earnings?.overtime_hours || 0) * (detail.earnings?.hourly_rate || 0) * 1.5)}</span>
                    </div>
                    {detail.earnings?.overtime_hours > 0 && (
                      <div className="flex justify-between">
                        <span>Overtime ({detail.earnings?.overtime_hours} hrs)</span>
                        <span>
                          {formatCurrency((detail.earnings?.overtime_hours || 0) * (detail.earnings?.hourly_rate || 0) * 1.5)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between font-semibold pt-1 border-t mt-1">
                      <span>Gross</span>
                      <span>{formatCurrency(detail.earnings?.gross_pay)}</span>
                    </div>
                  </div>
                </div>
                <div className="rounded-md border p-3">
                  <p className="text-xs text-muted-foreground mb-2 font-medium uppercase">
                    Deductions
                  </p>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span>CPP</span>
                      <span>-{formatCurrency(detail.deductions?.cpp)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>EI</span>
                      <span>-{formatCurrency(detail.deductions?.ei)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Federal Tax</span>
                      <span>-{formatCurrency(detail.deductions?.federal_tax)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Provincial Tax</span>
                      <span>-{formatCurrency(detail.deductions?.provincial_tax)}</span>
                    </div>
                    <div className="flex justify-between font-semibold pt-1 border-t mt-1 text-red-600">
                      <span>Total</span>
                      <span>-{formatCurrency(detail.deductions?.total)}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="rounded-md border-2 border-emerald-300 dark:border-emerald-700 p-4 bg-emerald-50/40 dark:bg-emerald-950/20 flex items-center justify-between">
                <span className="font-medium">Net Pay</span>
                <span className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">
                  {formatCurrency(detail.net_pay)}
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PayStubs;
